import { z } from 'zod';

/** The site the client talks to by default. */
export const WAYKITE_URL = 'https://waykite.com';

/** A location's page on the site, e.g. for a link beside a saved code. Needs no client or key. */
export const locationPageUrl = (code: string, baseUrl = WAYKITE_URL): string =>
  `${baseUrl.replace(/\/$/, '')}/view/locations/${encodeURIComponent(code)}`;

/** A facility's page on the site. Needs no client or key. */
export const facilityPageUrl = (code: string, baseUrl = WAYKITE_URL): string =>
  `${baseUrl.replace(/\/$/, '')}/view/facilities/${encodeURIComponent(code)}`;

/** A point, in decimal degrees. */
export const WaykiteCoordinatesSchema = z.object({
  lat: z.number().meta({ description: 'Latitude, in decimal degrees.' }),
  lon: z.number().meta({ description: 'Longitude, in decimal degrees.' }),
});
export type WaykiteCoordinates = z.infer<typeof WaykiteCoordinatesSchema>;

export const WaykiteLocationFunctionCodeSchema = z.enum([
  'port',
  'rail_terminal',
  'road_terminal',
  'airport',
  'postal_exchange',
  'multimodal',
  'fixed_transport',
  'border_crossing',
]);
export type WaykiteLocationFunctionCode = z.infer<typeof WaykiteLocationFunctionCodeSchema>;

export const WaykiteLocationStatusCodeSchema = z.enum([
  'AA',
  'AC',
  'AF',
  'AI',
  'AM',
  'AQ',
  'AS',
  'RL',
  'RN',
  'RQ',
  'RR',
  'QQ',
  'UR',
  'XX',
]);
export type WaykiteLocationStatusCode = z.infer<typeof WaykiteLocationStatusCodeSchema>;

export type WaykiteLocationFunction = {
  code: WaykiteLocationFunctionCode;
  label: string;
  description: string;
};
export const WAYKITE_LOCATION_FUNCTIONS: WaykiteLocationFunction[] = [
  { code: 'port', label: 'Port', description: 'Maritime port' }, // 1
  { code: 'rail_terminal', label: 'Rail Terminal', description: 'Rail terminal' }, // 2
  { code: 'road_terminal', label: 'Road Terminal', description: 'Road terminal' }, // 3
  { code: 'airport', label: 'Airport', description: 'Airport' }, // 4
  { code: 'postal_exchange', label: 'Postal Exchange', description: 'Postal exchange office' }, // 5
  { code: 'multimodal', label: 'Multimodal', description: 'Multimodal functions (ICDs, etc.)' }, // 6
  { code: 'fixed_transport', label: 'Fixed Transport', description: 'Fixed transport functions (e.g. pipeline)' }, // 7
  { code: 'border_crossing', label: 'Border Crossing', description: 'Border crossing function' }, // B
];

export const WaykiteLocationPortTypeSchema = z.enum([
  'seaport',
  'river_port',
  'lake_port',
  'dry_port',
  'offshore_terminal',
]);
export type WaykiteLocationPortType = z.infer<typeof WaykiteLocationPortTypeSchema>;

export const WaykiteLocationPortSizeSchema = z.enum(['very_small', 'small', 'medium', 'large']);
export type WaykiteLocationPortSize = z.infer<typeof WaykiteLocationPortSizeSchema>;

export const WaykiteLocationPortHarborTypeSchema = z.enum([
  'coastal_natural',
  'coastal_breakwater',
  'coastal_tide_gates',
  'river_natural',
  'river_basins',
  'river_tide_gates',
  'canal_or_lake',
  'open_roadstead',
  'typhoon_harbor',
]);
export type WaykiteLocationPortHarborType = z.infer<typeof WaykiteLocationPortHarborTypeSchema>;

export const WaykiteLocationPortHarborUseSchema = z.enum([
  'cargo',
  'military',
  'fishing',
  'marina',
  'passenger',
  'ferry',
]);
export type WaykiteLocationPortHarborUse = z.infer<typeof WaykiteLocationPortHarborUseSchema>;

export const WaykiteLocationPortHarborShelterSchema = z.enum(['excellent', 'good', 'fair', 'poor', 'none']);
export type WaykiteLocationPortHarborShelter = z.infer<typeof WaykiteLocationPortHarborShelterSchema>;

export const WaykiteLocationPortRepairCapabilitySchema = z.enum([
  'none',
  'emergency_only',
  'limited',
  'moderate',
  'major',
]);
export type WaykiteLocationPortRepairCapability = z.infer<typeof WaykiteLocationPortRepairCapabilitySchema>;

/**
 * A container terminal in the port. Its `smdg_code` is only unique together with the UN/LOCODE it is
 * listed under: that pair is what EDI messages carry (e.g. "DEHAM" + "EGH").
 */
export const WaykiteLocationContainerTerminalSchema = z.object({
  name: z.string().meta({ description: "The terminal's name.", example: 'EUROGATE CONTAINER TERMINAL HAMBURG' }),
  operator: z.string().optional().meta({ description: 'The company that runs it.' }),
  smdg_code: z.string().meta({
    description:
      'Its terminal code, unique together with the port code. See [Terminal codes](https://waykite.com/docs/guides/terminal-codes).',
    example: 'EGH',
  }),
  listed_under: z.string().optional().meta({
    description: "The port code that goes with `smdg_code`, when it is not this location's own.",
    example: 'CNNBO',
  }),
  coordinates: WaykiteCoordinatesSchema.meta({
    description: 'A point on the terminal in decimal degrees, close to the quay.',
  }),
  website: z.string().optional().meta({ description: "The terminal's website." }),
  address: z.string().optional().meta({ description: 'Its gate or visiting address.' }),
  notes: z.string().optional().meta({ description: 'Remarks, such as which berths it covers.' }),
});
export type WaykiteLocationContainerTerminal = z.infer<typeof WaykiteLocationContainerTerminalSchema>;

/** A depth, in metres. */
const depth = (what: string) =>
  z
    .number()
    .optional()
    .meta({ description: `Depth ${what}, in metres.` });
/** Whether the port has a facility or service; left out when not known. */
const has = (what: string) =>
  z
    .boolean()
    .optional()
    .meta({ description: `Whether the port has ${what}.` });

/**
 * The port's details and its container terminals. Unknown values are omitted, never `false` or 0.
 */
export const WaykiteLocationPortSchema = z.object({
  wpi_numbers: z
    .array(z.number())
    .optional()
    .meta({
      description:
        "The port's index numbers. When several ports share the location's code, the details come from the first.",
      example: [36990],
    }),
  type: WaykiteLocationPortTypeSchema.optional().meta({ description: 'What kind of port it is.' }),
  harbor: z
    .object({
      size: WaykiteLocationPortSizeSchema.optional().meta({ description: 'How big the harbour is.' }),
      type: WaykiteLocationPortHarborTypeSchema.optional().meta({ description: 'How the harbour is formed.' }),
      use: WaykiteLocationPortHarborUseSchema.optional().meta({ description: 'What the harbour is mainly used for.' }),
      shelter: WaykiteLocationPortHarborShelterSchema.optional().meta({
        description: 'How well the harbour shelters vessels.',
      }),
    })
    .optional()
    .meta({ description: 'The harbour: its size, type, use and shelter.' }),
  region: z.string().optional().meta({ description: 'The coastal region the port is in.', example: 'Netherlands' }),
  navigation_area: z
    .string()
    .optional()
    .meta({ description: 'The NAVAREA for navigational warnings, as a Roman numeral.', example: 'I' }),
  first_port_of_entry: z
    .boolean()
    .optional()
    .meta({ description: 'Whether vessels arriving from abroad can clear in here.' }),
  repair_capability: WaykiteLocationPortRepairCapabilitySchema.optional().meta({
    description: 'What repairs the port can carry out.',
  }),
  tidal_range_meters: z.number().optional().meta({ description: 'The usual tidal range, in metres.' }),
  depths: z
    .object({
      channel_meters: depth('in the entrance channel'),
      anchorage_meters: depth('at the anchorage'),
      cargo_handling_meters: depth('alongside the cargo quays'),
    })
    .optional()
    .meta({ description: 'Water depths, in metres.' }),
  max_vessel: z
    .object({
      length_meters: z.number().optional().meta({ description: 'Length overall, in metres.' }),
      beam_meters: z.number().optional().meta({ description: 'Beam, in metres.' }),
      draft_meters: z.number().optional().meta({ description: 'Draft, in metres.' }),
    })
    .optional()
    .meta({ description: 'The largest vessel the port takes.' }),
  terminals: z
    .object({
      container: has('a container terminal'),
      ro_ro: has('a roll-on/roll-off terminal'),
      solid_bulk: has('a solid bulk terminal'),
      liquid_bulk: has('a liquid bulk terminal'),
      oil: has('an oil terminal'),
      lng: has('an LNG terminal'),
    })
    .optional()
    .meta({ description: 'The kinds of cargo terminal the port has.' }),
  facilities: z
    .object({
      drydock: has('a dry dock'),
      medical: has('medical facilities'),
      garbage_disposal: has('garbage disposal'),
      bunker_fuel: has('bunker fuel'),
      diesel: has('diesel'),
      water: has('fresh water'),
      tugs: has('tugs'),
      pilotage_available: has('pilots available'),
      pilotage_compulsory: z.boolean().optional().meta({ description: 'Whether taking a pilot is compulsory.' }),
      port_security: has('port security'),
      vessel_traffic_service: has('a vessel traffic service'),
      traffic_separation_scheme: has('a traffic separation scheme in its approaches'),
    })
    .optional()
    .meta({ description: 'Services and facilities at the port.' }),
  container_terminals: z
    .array(WaykiteLocationContainerTerminalSchema)
    .optional()
    .meta({ description: "The port's container terminals, by name." }),
});
export type WaykiteLocationPort = z.infer<typeof WaykiteLocationPortSchema>;

export const WaykiteLocationAirportTypeSchema = z.enum(['large', 'medium', 'small', 'heliport', 'seaplane_base']);
export type WaykiteLocationAirportType = z.infer<typeof WaykiteLocationAirportTypeSchema>;

export const WaykiteLocationRunwaySurfaceSchema = z.enum([
  'asphalt',
  'concrete',
  'grass',
  'gravel',
  'dirt',
  'sand',
  'water',
  'snow',
  'other',
]);
export type WaykiteLocationRunwaySurface = z.infer<typeof WaykiteLocationRunwaySurfaceSchema>;

export const WaykiteLocationRunwaySchema = z.object({
  designator: z
    .string()
    .meta({ description: "The runway's designator: its two ends, by heading.", example: '18R/36L' }),
  length_meters: z.number().optional().meta({ description: 'Length, in metres.' }),
  width_meters: z.number().optional().meta({ description: 'Width, in metres.' }),
  surface: WaykiteLocationRunwaySurfaceSchema.optional().meta({ description: 'What the runway is surfaced with.' }),
  paved: z.boolean().optional().meta({ description: 'Whether the surface is paved, from `surface`.' }),
  lighted: z.boolean().optional().meta({ description: 'Whether the runway is lit.' }),
});
export type WaykiteLocationRunway = z.infer<typeof WaykiteLocationRunwaySchema>;

/** The airport's details, matched on IATA code. Unknown values are omitted. */
export const WaykiteLocationAirportSchema = z.object({
  name: z.string().optional().meta({ description: "The airport's name.", example: 'Amsterdam Airport Schiphol' }),
  icao: z.string().optional().meta({
    description:
      "The airport's ICAO code. See [UN/LOCODE vs IATA vs ICAO](https://waykite.com/docs/guides/unlocode-iata-icao).",
    example: 'EHAM',
  }),
  type: WaykiteLocationAirportTypeSchema.optional().meta({ description: 'How big the airport is, or what kind.' }),
  scheduled_service: z.boolean().optional().meta({ description: 'Whether airlines fly scheduled services to it.' }),
  elevation_meters: z.number().optional().meta({ description: 'Elevation above sea level, in metres.' }),
  runways_summary: z
    .object({
      count: z.number().meta({ description: 'How many runways are open.' }),
      longest_meters: z.number().optional().meta({ description: 'The longest open runway, in metres.' }),
      longest_paved: z.boolean().optional().meta({ description: 'Whether the longest open runway is paved.' }),
    })
    .optional()
    .meta({ description: 'The open runways at a glance.' }),
  runways: z.array(WaykiteLocationRunwaySchema).optional().meta({ description: 'The open runways, longest first.' }),
});
export type WaykiteLocationAirport = z.infer<typeof WaykiteLocationAirportSchema>;

export const WaykiteLocationModalitiesSchema = z.object({
  port: WaykiteLocationPortSchema.optional().meta({ description: 'Port details, for a location that is a port.' }),
  airport: WaykiteLocationAirportSchema.optional().meta({
    description: 'Airport details, for a location that is an airport.',
  }),
});
export type WaykiteLocationModalities = z.infer<typeof WaykiteLocationModalitiesSchema>;

export const WaykiteLocationSchema = z.object({
  code: z.string().meta({ description: 'The UN/LOCODE: `country` followed by `location`.', example: 'USNYC' }),
  country: z.string().meta({ description: 'ISO 3166-1 alpha-2 country code.', example: 'US' }),
  location: z.string().meta({ description: 'The three characters after the country.', example: 'NYC' }),
  name: z.string().meta({ description: 'The name without diacritics.', example: 'Kobenhavn' }),
  name_native: z
    .string()
    .optional()
    .meta({ description: 'The name with its diacritics, when it differs from `name`.', example: 'København' }),
  display_name: z.string().meta({
    description:
      'The name with the English country name, ready to show people. A city state is not repeated: `Singapore`.',
    example: 'Rotterdam, Netherlands',
  }),
  subdivision: z
    .string()
    .meta({ description: 'ISO 3166-2 subdivision code without the country, or an empty string.', example: 'NY' }),
  functions: WaykiteLocationFunctionCodeSchema.array().meta({
    description:
      'What the location handles. Empty when that is not known. See [Function codes](https://waykite.com/docs/guides/function-codes).',
  }),
  status: WaykiteLocationStatusCodeSchema.meta({
    description:
      'How far the entry has been checked or approved. See [Status codes](https://waykite.com/docs/guides/status-codes).',
  }),
  iata: z.string().optional().meta({
    description:
      'The IATA location code, when it differs from `location`, e.g. `QAJ` for `AEAJM`. See [UN/LOCODE vs IATA vs ICAO](https://waykite.com/docs/guides/unlocode-iata-icao).',
    example: 'QAJ',
  }),
  exonyms: z
    .array(z.string())
    .optional()
    .meta({ description: 'Other names the place is known by.', example: ['Copenhagen'] }),
  coordinates: WaykiteCoordinatesSchema.nullable().meta({
    description: 'The position in decimal degrees, or `null` when it is not known.',
    example: { lat: 40.7, lon: -74 },
  }),
  modalities: WaykiteLocationModalitiesSchema.optional().meta({
    description:
      'Details for some functions: `port` and `airport`. Unknown values are left out rather than sent as `false` or `0`.',
  }),
});
export type WaykiteLocation = z.infer<typeof WaykiteLocationSchema>;

/** The kinds of facility. */
export const WaykiteFacilityTypeSchema = z.enum([
  'container_terminal',
  'lng_terminal',
  'coal_terminal',
  'oil_terminal',
  'bulk_terminal',
  'terminal',
  'anchorage',
  'sts_area',
]);
export type WaykiteFacilityType = z.infer<typeof WaykiteFacilityTypeSchema>;

export const WAYKITE_FACILITY_TYPE_LABELS: Record<WaykiteFacilityType, string> = {
  container_terminal: 'Container terminal',
  lng_terminal: 'LNG terminal',
  coal_terminal: 'Coal terminal',
  oil_terminal: 'Oil terminal',
  bulk_terminal: 'Bulk terminal',
  terminal: 'Terminal',
  anchorage: 'Anchorage',
  sts_area: 'STS area',
};

/** Where a facility stands: running, being built, planned, out of use or closed for good. */
export const WaykiteFacilityStatusSchema = z.enum([
  'operating',
  'construction',
  'idle',
  'proposed',
  'shelved',
  'retired',
]);
export type WaykiteFacilityStatus = z.infer<typeof WaykiteFacilityStatusSchema>;

export const WAYKITE_FACILITY_STATUS_LABELS: Record<WaykiteFacilityStatus, string> = {
  operating: 'Operating',
  construction: 'Under construction',
  idle: 'Idle',
  proposed: 'Proposed',
  shelved: 'Shelved',
  retired: 'Retired',
};

/** The kinds of cargo a facility handles. */
export const WaykiteFacilityCargoSchema = z.enum([
  'crude_oil',
  'petroleum_products',
  'chemicals',
  'liquefied_gas',
  'dry_bulk',
  'general_cargo',
  'containers',
  'vehicles',
  'passengers',
]);
export type WaykiteFacilityCargo = z.infer<typeof WaykiteFacilityCargoSchema>;

export const WAYKITE_FACILITY_CARGO_LABELS: Record<WaykiteFacilityCargo, string> = {
  crude_oil: 'Crude oil',
  petroleum_products: 'Petroleum products',
  chemicals: 'Chemicals',
  liquefied_gas: 'Liquefied gas',
  dry_bulk: 'Dry bulk',
  general_cargo: 'General cargo',
  containers: 'Containers',
  vehicles: 'Vehicles',
  passengers: 'Passengers',
};

/** Which way cargo moves through a facility: in from abroad, out to it, or within the country. */
export const WaykiteFacilityDirectionSchema = z.enum(['import', 'export', 'domestic']);
export type WaykiteFacilityDirection = z.infer<typeof WaykiteFacilityDirectionSchema>;

export const WAYKITE_FACILITY_DIRECTION_LABELS: Record<WaykiteFacilityDirection, string> = {
  import: 'Import',
  export: 'Export',
  domestic: 'Domestic',
};

/**
 * The water a facility covers, such as an anchorage, as a GeoJSON MultiPolygon: `[lon, lat]` positions, the first ring
 * of each polygon its outline and any others holes in it.
 */
export const WaykiteFacilityAreaSchema = z.object({
  type: z.literal('MultiPolygon'),
  coordinates: z.number().array().array().array().array(),
});
export type WaykiteFacilityArea = z.infer<typeof WaykiteFacilityAreaSchema>;

/** The port nearest a facility, and how far it is. */
export const WaykiteNearestPortSchema = z.object({
  unlocode: z.string().meta({ description: "The port's UN/LOCODE.", example: 'DEHAM' }),
  distance_km: z.number().meta({ description: 'How far the port is from the facility, in kilometres.', example: 2.4 }),
});
export type WaykiteNearestPort = z.infer<typeof WaykiteNearestPortSchema>;

/**
 * A facility (a terminal, jetty, anchorage or offshore unit) as the facilities API and dataset file return it,
 * identified by its `code` alone. It has a `unlocode` when it is part of a UN/LOCODE location, such as a terminal
 * in a port; one offshore or away from any port has none. Every facility has a `nearest_port`.
 *
 * UN/LOCODE entries embed their container terminals as `modalities.port.container_terminals`, without the
 * fields the entry already gives (see WaykiteLocationContainerTerminalSchema).
 */
export const WaykiteFacilitySchema = z.object({
  code: z.string().meta({
    description:
      "The facility's code, unique on its own: a container terminal's UN/LOCODE and terminal code together (`DEHAMEGH`), or a code starting with `LNG` or `COAL` for an LNG or coal terminal.",
    example: 'DEHAMEGH',
  }),
  type: WaykiteFacilityTypeSchema.meta({ description: 'The kind of facility.' }),
  status: WaykiteFacilityStatusSchema.meta({
    description:
      'Where it stands: `operating`, `construction`, `idle` (out of use, but could run again), `proposed`, `shelved` (a proposal on hold) or `retired`.',
  }),
  unlocode: z.string().optional().meta({
    description:
      'The UN/LOCODE of the location the facility is part of, such as its port. Left out for a facility that is not part of one, such as a terminal far offshore.',
    example: 'DEHAM',
  }),
  smdg_code: z.string().optional().meta({
    description:
      "A container terminal's terminal code, unique together with `unlocode`: the pair EDI messages carry. See [Terminal codes](https://waykite.com/docs/guides/terminal-codes).",
    example: 'EGH',
  }),
  alternative_unlocode: z.string().optional().meta({
    description:
      'Another port code the industry uses for the facility, e.g. `CNNBG` beside `CNNBO`. Searching by either finds it.',
    example: 'CNNBG',
  }),
  country: z.string().meta({ description: 'ISO 3166-1 alpha-2 country code.', example: 'DE' }),
  name: z.string().meta({ description: "The facility's name.", example: 'EUROGATE CONTAINER TERMINAL HAMBURG' }),
  display_name: z.string().meta({
    description: 'The name for reading, with acronyms kept.',
    example: 'Eurogate Container Terminal Hamburg',
  }),
  operator: z.string().optional().meta({ description: 'The company that runs it.' }),
  cargo: WaykiteFacilityCargoSchema.array()
    .optional()
    .meta({
      description:
        'The kinds of cargo it handles: `crude_oil`, `petroleum_products`, `chemicals`, `liquefied_gas` (LNG, LPG, ammonia), `dry_bulk`, `general_cargo`, `containers`, `vehicles` or `passengers`. Left out when not known.',
      example: ['containers'],
    }),
  depth_m: z.number().optional().meta({
    description: 'The greatest depth of water alongside, in metres. Left out when not known.',
    example: 12.2,
  }),
  directions: WaykiteFacilityDirectionSchema.array()
    .optional()
    .meta({
      description: 'Which way its cargo moves: `import`, `export` or `domestic`, one or more. Left out when not known.',
      example: ['import'],
    }),
  capacity_mtpa: z.number().optional().meta({
    description:
      'How much it can handle, in million tonnes a year: for one in operation, its running units; for one planned, what is planned. Left out when not known.',
    example: 5,
  }),
  floating: z.boolean().optional().meta({
    description: 'Present (`true`) for a floating unit, such as a floating storage and regasification unit (FSRU).',
  }),
  offshore: z.boolean().optional().meta({ description: 'Present (`true`) for a facility offshore.' }),
  coordinates: WaykiteCoordinatesSchema.meta({
    description:
      'A point on the facility in decimal degrees: close to the quay for a terminal, the middle of an area for an anchorage.',
    example: { lat: 53.53, lon: 9.92 },
  }),
  area: WaykiteFacilityAreaSchema.optional().meta({
    description:
      'For a facility that covers water rather than standing at a point, such as an anchorage or STS area: its outline as a GeoJSON MultiPolygon, `[lon, lat]` positions, simplified to about 20 m.',
  }),
  nearest_port: WaykiteNearestPortSchema.meta({
    description:
      "The nearest UN/LOCODE location with the port function, preferring one in the facility's own country within 50 km. It can be far away, or differ from `unlocode`.",
  }),
  website: z.string().optional().meta({ description: "The facility's website." }),
  address: z.string().optional().meta({ description: 'Its gate or visiting address.' }),
  notes: z.string().optional().meta({ description: 'Remarks, such as which berths it covers.' }),
});
export type WaykiteFacility = z.infer<typeof WaykiteFacilitySchema>;

export const WaykiteLocationSearchResultSchema = z.object({
  results: WaykiteLocationSchema.array(),
  total: z.number(),
});
export type WaykiteLocationSearchResult = z.infer<typeof WaykiteLocationSearchResultSchema>;

export const WaykiteNearbyLocationSchema = WaykiteLocationSchema.extend({
  distance_km: z.number().meta({ description: 'How far away it is, in kilometres.' }),
});
export type WaykiteNearbyLocation = z.infer<typeof WaykiteNearbyLocationSchema>;

const NearbyLocationsSchema = z.object({ results: WaykiteNearbyLocationSchema.array() });

export const WaykiteLocationDatasetSchema = z.object({
  dataset_version: z.string(), // e.g. "2025-1"
  generated_at: z.string(), // when the file was built, ISO 8601
  entries: WaykiteLocationSchema.array(),
});
export type WaykiteLocationDataset = z.infer<typeof WaykiteLocationDatasetSchema>;

/**
 * A facility as search and nearby return it: everything but its `area`, which can run to thousands of positions. Get a
 * facility by its code for its area.
 */
export const WaykiteFacilitySummarySchema = WaykiteFacilitySchema.omit({ area: true });
export type WaykiteFacilitySummary = z.infer<typeof WaykiteFacilitySummarySchema>;

export const WaykiteFacilitySearchResultSchema = z.object({
  results: WaykiteFacilitySummarySchema.array(),
  total: z.number(),
});
export type WaykiteFacilitySearchResult = z.infer<typeof WaykiteFacilitySearchResultSchema>;

export const WaykiteNearbyFacilitySchema = WaykiteFacilitySummarySchema.extend({
  distance_km: z.number().meta({ description: 'How far away it is, in kilometres.' }),
});
export type WaykiteNearbyFacility = z.infer<typeof WaykiteNearbyFacilitySchema>;

const NearbyFacilitiesSchema = z.object({ results: WaykiteNearbyFacilitySchema.array() });

export const WaykiteFacilityDatasetSchema = z.object({
  dataset_version: z.string(), // the day its facilities last changed, e.g. "2026-10-02"
  generated_at: z.string(), // when the file was built, ISO 8601
  entries: WaykiteFacilitySchema.array(),
});
export type WaykiteFacilityDataset = z.infer<typeof WaykiteFacilityDatasetSchema>;

/** The kinds of ship, by the first digit of the ship type it broadcasts. */
export const WaykiteShipCategorySchema = z.enum(['passenger', 'cargo', 'tanker', 'service', 'fishing', 'other']);
export type WaykiteShipCategory = z.infer<typeof WaykiteShipCategorySchema>;

export const WAYKITE_SHIP_CATEGORY_LABELS: Record<WaykiteShipCategory, string> = {
  passenger: 'Passenger',
  cargo: 'Cargo',
  tanker: 'Tanker',
  service: 'Service',
  fishing: 'Fishing',
  other: 'Other',
};

const WaykiteShipSchema = z.object({
  mmsi: z.number().meta({ description: "The ship's MMSI, the number it broadcasts under." }),
  imo: z.number().nullable().meta({ description: "The ship's IMO number, when it sends a valid one." }),
  name: z.string().nullable().meta({ description: "The ship's name, as it broadcasts it." }),
  ship_type: z.number().nullable().meta({ description: 'The ship type it broadcasts, e.g. 70 for cargo.' }),
  ship_category: WaykiteShipCategorySchema.meta({
    description:
      'The kind of ship, from its type: `passenger` (60-69), `cargo` (70-79), `tanker` (80-89), `service` (50-59: pilots, rescue, tugs, port tenders and the like), `fishing` (30), or `other`, ships that never sent their type included.',
  }),
  destination_raw: z.string().nullable().meta({ description: 'The destination as the crew typed it.' }),
});

export const WaykitePortArrivalSchema = WaykiteShipSchema.extend({
  probability: z
    .number()
    .meta({ description: 'How likely the destination the crew typed means this port, from 0 to 1.' }),
  eta: z.string().meta({
    description: 'When we expect the ship, ISO 8601.',
  }),
  reported_eta: z.string().nullable().meta({ description: "The crew's ETA as broadcast, ISO 8601." }),
  predicted_at: z.string().meta({ description: 'When the estimate was made, ISO 8601.' }),
  position: z
    .object({ at: z.string(), lat: z.number(), lon: z.number(), speed_kn: z.number().nullable() })
    .nullable()
    .meta({ description: 'Where the ship was, and how fast it went in knots, when the estimate was made.' }),
});
export type WaykitePortArrival = z.infer<typeof WaykitePortArrivalSchema>;

export const WaykitePortCallSchema = WaykiteShipSchema.extend({
  arrived_at: z.string().meta({ description: 'When the ship entered the port, or was first seen in it, ISO 8601.' }),
  departed_at: z.string().nullable().meta({ description: 'When it left, ISO 8601; `null` while it is still there.' }),
  arrival_seen: z
    .boolean()
    .meta({ description: 'Whether the ship was seen coming in, not first seen already in port.' }),
  gap: z.boolean().meta({ description: 'Whether the ship went unheard for a while during the call.' }),
  stops: z
    .object({
      kind: z.enum(['anchored', 'berthed']),
      area: z.string().optional().meta({ description: 'The terminal or anchorage it stopped in, by its code.' }),
      area_name: z.string().optional().meta({ description: "That terminal's or anchorage's name." }),
      start: z.string(),
      end: z.string().nullable(),
    })
    .array()
    .meta({ description: 'Where the ship stopped in the port, at anchor or at a berth, in order.' }),
});
export type WaykitePortCall = z.infer<typeof WaykitePortCallSchema>;

const portAnswer = {
  unlocode: z.string().meta({ description: 'The code asked for.' }),
  port: z.string().meta({
    description: "The port that answered: the code asked for, or the larger port it is part of, such as a terminal's.",
  }),
  within_hours: z.number().int(),
};

export const WaykitePortArrivalsSchema = z.object({
  ...portAnswer,
  arrivals: WaykitePortArrivalSchema.array(),
  calls_last_30_days: z.number().meta({
    description:
      'How many calls cargo ships and tankers, the ships arrivals are estimated for, began at the port in the last 30 days: a sign of how well they are heard there. Few or none means the list is thin.',
  }),
});
export type WaykitePortArrivals = z.infer<typeof WaykitePortArrivalsSchema>;

export const WaykitePortCallsSchema = z.object({ ...portAnswer, calls: WaykitePortCallSchema.array() });
export type WaykitePortCalls = z.infer<typeof WaykitePortCallsSchema>;

const vesselNumbers = {
  mmsi: z.number().meta({ description: "The ship's MMSI, the number it broadcasts under." }),
  imo: z.number().nullable().meta({ description: "The ship's IMO number, when it sends a valid one." }),
};

/** A ship: who it is. */
/** A ship by a typed identifier: `imo:` and its IMO number, or `mmsi:` and its MMSI. */
export const WaykiteVesselIdSchema = z.union(
  [z.templateLiteral(['imo:', z.string().regex(/^\d{7}$/)]), z.templateLiteral(['mmsi:', z.string().regex(/^\d{9}$/)])],
  { error: 'Use imo: and an IMO number (7 digits) or mmsi: and an MMSI (9 digits).' },
);
export type WaykiteVesselId = z.infer<typeof WaykiteVesselIdSchema>;

export const WaykiteVesselSchema = WaykiteShipSchema.omit({ destination_raw: true }).extend({
  call_sign: z.string().nullable().meta({ description: "The ship's radio call sign." }),
  length_m: z.number().nullable().meta({ description: 'Its length overall, in metres.' }),
  beam_m: z.number().nullable().meta({ description: 'Its beam, in metres.' }),
});
export type WaykiteVessel = z.infer<typeof WaykiteVesselSchema>;

/** Where a ship was last heard, and the port it is in. */
export const WaykiteVesselPositionSchema = z.object({
  ...vesselNumbers,
  at: z.string().meta({
    description:
      'When the ship was heard there, ISO 8601. A ship that goes unheard keeps the position it was last heard at.',
  }),
  lat: z.number(),
  lon: z.number(),
  speed_kn: z.number().nullable().meta({ description: 'Its speed over the ground, in knots.' }),
  course_deg: z.number().nullable().meta({ description: 'Its course over the ground, in degrees.' }),
  heading_deg: z.number().nullable().meta({ description: 'Where its bow points, in degrees.' }),
  nav_status: z.number().nullable().meta({
    description: 'Its navigational status as broadcast, e.g. 0 under way, 1 at anchor, 5 moored.',
  }),
  in_port: z
    .object({ unlocode: z.string(), arrived_at: z.string() })
    .nullable()
    .meta({ description: 'The port it is in, and since when.' }),
});
export type WaykiteVesselPosition = z.infer<typeof WaykiteVesselPositionSchema>;

/** Where a ship is going, and when: as its crew reports it, and as we expect it. */
export const WaykiteVesselVoyageSchema = z.object({
  ...vesselNumbers,
  destination_raw: z.string().nullable().meta({ description: 'The destination as the crew typed it.' }),
  reported_eta: z.string().nullable().meta({ description: "The crew's ETA as broadcast, ISO 8601." }),
  draught_m: z.number().nullable().meta({ description: 'Its draught as the crew last set it, in metres.' }),
  next_port: z
    .object({
      unlocode: z.string(),
      probability: z.number(),
      eta: z.string(),
      predicted_at: z.string(),
    })
    .nullable()
    .meta({
      description:
        'The port its destination most likely means, how likely, and when we expect it there, while a cargo ship or tanker is at sea.',
    }),
});
export type WaykiteVesselVoyage = z.infer<typeof WaykiteVesselVoyageSchema>;

const tonnes = (description: string) =>
  z
    .number()
    .optional()
    .meta({ description: `${description}, in tonnes.` });

/** One gas a ship emitted over a report's period, in tonnes, by where it was emitted. */
export const WaykiteEmissionsSchema = z.object({
  total: tonnes('All of it'),
  between_ports: tonnes('On voyages between two EU or EEA ports'),
  departing: tonnes('On voyages from an EU or EEA port to a port elsewhere'),
  arriving: tonnes('On voyages to an EU or EEA port from a port elsewhere'),
  at_berth: tonnes('At berth in EU or EEA ports'),
  in_port: tonnes('Within EU or EEA ports, at berth or moving (reported from 2024)'),
  on_laden: tonnes('On voyages with cargo aboard'),
  passenger_transport: tonnes('Assigned to carrying passengers, for a ship that carries both'),
  freight_transport: tonnes('Assigned to carrying freight, for a ship that carries both'),
  emissions_trading: tonnes("The part counted for the EU's emissions trading system (reported from 2024)"),
});
export type WaykiteEmissions = z.infer<typeof WaykiteEmissionsSchema>;

const efficiencyFields = {
  per_distance: z.number().optional().meta({ description: 'Per nautical mile, in kilograms.' }),
  per_tonne_mile: z.number().optional().meta({ description: 'Per tonne of cargo carried a nautical mile, in grams.' }),
  per_cubic_metre_mile: z
    .number()
    .optional()
    .meta({ description: 'Per cubic metre of cargo carried a nautical mile, in grams.' }),
  per_dwt_mile: z
    .number()
    .optional()
    .meta({ description: 'Per tonne of deadweight carried a nautical mile, in grams.' }),
  per_passenger_mile: z.number().optional().meta({ description: 'Per passenger carried a nautical mile, in grams.' }),
  per_freight_tonne_mile: z
    .number()
    .optional()
    .meta({ description: 'Per tonne of freight carried a nautical mile, in grams.' }),
};

/** How much fuel a ship burned, or a gas it emitted, for the work it did. Only the measures for its trade are given. */
export const WaykiteEfficiencySchema = z.object({
  ...efficiencyFields,
  per_hour_at_sea: z.number().optional().meta({ description: 'Per hour at sea, in tonnes (reported from 2024).' }),
  laden: z
    .object(efficiencyFields)
    .optional()
    .meta({ description: 'The same measures over voyages with cargo aboard only.' }),
});
export type WaykiteEfficiency = z.infer<typeof WaykiteEfficiencySchema>;

/**
 * A ship's report of the fuel it burned and the greenhouse gases it emitted over a year, or part of one, on
 * voyages to, from and between ports in the EU and the EEA, and in those ports. Fields not reported are left out.
 */
export const WaykiteVesselEmissionsReportSchema = z.object({
  year: z.number().int().meta({ description: 'The year the report is for.', example: 2024 }),
  period: z
    .object({
      from: z.string().meta({ description: 'The first day it covers, YYYY-MM-DD.', example: '2024-01-01' }),
      to: z.string().meta({ description: 'The last day it covers, YYYY-MM-DD.', example: '2024-12-31' }),
    })
    .meta({ description: 'The days it covers: the whole year, or part of it for a partial report.' }),
  partial: z.boolean().meta({
    description: 'Whether it covers only part of the year, as when the ship changed hands or left EU trade.',
  }),
  name: z.string().meta({ description: "The ship's name in the report.", example: 'EVER GIVEN' }),
  ship_type: z.string().meta({ description: 'Its type as reported.', example: 'Container ship' }),
  technical_efficiency: z
    .object({
      index: z.string().meta({ description: 'Which index: `EEDI`, `EEXI` or `EIV`.', example: 'EEDI' }),
      value: z.number().meta({ description: 'Its value, in grams of CO₂ per tonne-mile.', example: 4.12 }),
    })
    .optional()
    .meta({ description: 'Its design energy efficiency.' }),
  port_of_registry: z.string().optional().meta({ description: 'Its port of registry.' }),
  home_port: z.string().optional().meta({ description: 'Its home port.' }),
  ice_class: z.string().optional().meta({ description: 'Its ice class.' }),
  company: z
    .object({
      imo: z.string().optional().meta({ description: "The company's IMO number." }),
      name: z.string().meta({ description: "The company's name." }),
    })
    .optional()
    .meta({ description: 'The company responsible for the ship over the period (reported from 2024).' }),
  fuel: z
    .object({
      total: tonnes('All fuel burned'),
      on_laden: tonnes('Burned on voyages with cargo aboard'),
      cargo_heating: tonnes('Burned to heat cargo (reported from 2024)'),
      dynamic_positioning: tonnes('Burned holding position (reported from 2024)'),
    })
    .meta({ description: 'The fuel it burned, in tonnes.' }),
  co2: WaykiteEmissionsSchema.meta({ description: 'Carbon dioxide.' }),
  ch4: WaykiteEmissionsSchema.optional().meta({ description: 'Methane (reported from 2024).' }),
  n2o: WaykiteEmissionsSchema.optional().meta({ description: 'Nitrous oxide (reported from 2024).' }),
  co2eq: WaykiteEmissionsSchema.optional().meta({
    description: 'All three as CO₂-equivalent, weighted by how much each warms (reported from 2024).',
  }),
  hours_at_sea: z.number().optional().meta({ description: 'Hours it spent at sea.' }),
  hours_at_sea_in_ice: z.number().optional().meta({ description: 'Hours of those it spent sailing through ice.' }),
  miles_in_ice: z.number().optional().meta({ description: 'Nautical miles it sailed through ice.' }),
  fuel_efficiency: WaykiteEfficiencySchema.meta({ description: 'Fuel burned for the work it did.' }),
  co2_efficiency: WaykiteEfficiencySchema.meta({ description: 'CO₂ emitted for the work it did.' }),
  co2eq_efficiency: WaykiteEfficiencySchema.optional().meta({
    description: 'CO₂-equivalent emitted for the work it did (reported from 2024).',
  }),
});
export type WaykiteVesselEmissionsReport = z.infer<typeof WaykiteVesselEmissionsReportSchema>;

/** A ship's emissions reports, newest first. */
export const WaykiteVesselEmissionsSchema = z.object({
  imo: z.number().meta({ description: 'The IMO number the reports are filed under.' }),
  reports: WaykiteVesselEmissionsReportSchema.array().meta({
    description: 'Its reports, newest first. Empty for a ship that has not reported.',
  }),
});
export type WaykiteVesselEmissions = z.infer<typeof WaykiteVesselEmissionsSchema>;

/** The events a webhook endpoint can ask for. */
export const WaykiteWebhookEventTypeSchema = z.enum([
  'port.arrival.expected',
  'port.call.arrived',
  'port.call.departed',
  'vessel.arrival.expected',
  'vessel.call.arrived',
  'vessel.call.departed',
  'watch.created',
  'watch.updated',
  'watch.deleted',
]);
export type WaykiteWebhookEventType = z.infer<typeof WaykiteWebhookEventTypeSchema>;

/** What of a watched port or ship the organization's webhook endpoints hear about: `ais`, its ships' arrivals and calls. */
export const WaykiteWatchTopicSchema = z.enum(['ais']);
export type WaykiteWatchTopic = z.infer<typeof WaykiteWatchTopicSchema>;

export const WaykiteWatchSchema = z.object({
  id: z.string(),
  port: z
    .string()
    .nullable()
    .meta({ description: 'The port watched, by UN/LOCODE: for one that is part of a larger port, that port.' }),
  vessel: WaykiteVesselIdSchema.nullable().meta({
    description: 'The ship watched, by the identifier it was added by: `imo:` or `mmsi:`.',
  }),
  name: z.string().nullable().meta({ description: "The port's name, or the ship's as it broadcasts it." }),
  topics: WaykiteWatchTopicSchema.array().meta({ description: 'What of it the webhook endpoints hear about.' }),
  created_at: z.string().meta({ description: 'ISO 8601.' }),
});
export type WaykiteWatch = z.infer<typeof WaykiteWatchSchema>;

export const WaykiteWebhookEndpointSchema = z.object({
  id: z.string(),
  url: z.string().meta({ description: 'Where events are sent: https, or http to localhost for trying a receiver.' }),
  description: z.string().nullable(),
  events: WaykiteWebhookEventTypeSchema.array().meta({ description: 'The events it is sent.' }),
  state: z.enum(['active', 'failing', 'off']).meta({
    description:
      '`failing` from its first failure after a success, `off` once turned off, by you or after five days without a success.',
  }),
  failing_since: z.string().nullable().meta({ description: 'ISO 8601.' }),
  disabled_at: z.string().nullable().meta({ description: 'When it was turned off, ISO 8601.' }),
  previous_secret_until: z.string().nullable().meta({
    description: "After a roll, until when requests carry the previous secret's signature too, ISO 8601.",
  }),
  created_at: z.string().meta({ description: 'ISO 8601.' }),
});
export type WaykiteWebhookEndpoint = z.infer<typeof WaykiteWebhookEndpointSchema>;

export const WaykiteWebhookSecretSchema = z.object({
  secret: z.string().meta({ description: 'The signing secret, `whsec_` and base64.' }),
  previous: z.string().nullable().meta({ description: 'The previous secret, while it still signs after a roll.' }),
});
export type WaykiteWebhookSecret = z.infer<typeof WaykiteWebhookSecretSchema>;

export const WaykiteWebhookDeliverySchema = z.object({
  id: z.string().meta({ description: 'The `webhook-id` header sent with it.' }),
  endpoint_id: z.string(),
  event_type: z.string().meta({ description: 'An event type, or `webhook.test`.' }),
  status: z.enum(['pending', 'delivered', 'failed']).meta({
    description: '`pending` until it is delivered, or until its last retry fails.',
  }),
  attempts: z.number().int(),
  next_attempt_at: z.string().nullable().meta({ description: 'When it is tried next, ISO 8601.' }),
  last_attempt_at: z.string().nullable(),
  last_status: z.number().int().nullable().meta({ description: "The endpoint's last HTTP status, if it answered." }),
  last_error: z.string().nullable().meta({ description: 'Why the last try got no answer, such as a timeout.' }),
  created_at: z.string().meta({ description: 'ISO 8601.' }),
});
export type WaykiteWebhookDelivery = z.infer<typeof WaykiteWebhookDeliverySchema>;

export type WaykiteWebhookEndpointParams = {
  url: string;
  description?: string | null;
  events: WaykiteWebhookEventType[];
};

export type WaykitePortArrivalsParams = {
  /** How far ahead to look, in hours, up to 720. @default 168 */
  withinHours?: number;
};

export type WaykitePortCallsParams = {
  /** How far back to look for calls that ended, in hours, up to 2160 (90 days). @default 168 */
  withinHours?: number;
  /** Only the calls of ships of these kinds. */
  types?: WaykiteShipCategory[] | null;
};

export type WaykiteLocationSearchParams = {
  /** A name or code to look for, in part. */
  query?: string;
  /** Only locations in these countries (ISO 3166-1 alpha-2). */
  countries?: string[] | null;
  /** Only locations with at least one of these functions. */
  functions?: WaykiteLocationFunctionCode[] | null;
  /** @default 50 */
  limit?: number;
  /** @default 0 */
  offset?: number;
};

export type WaykiteNearbyLocationParams = {
  lat: number;
  lon: number;
  countries?: string[] | null;
  functions?: WaykiteLocationFunctionCode[] | null;
  /** How far to look, in kilometres. */
  maxDistanceKm?: number;
  /** @default 10 */
  limit?: number;
};

export type WaykiteFacilitySearchParams = {
  /** A name, operator or terminal code to look for, in part. */
  query?: string;
  /** Only facilities in these countries (ISO 3166-1 alpha-2). */
  countries?: string[] | null;
  /** Only facilities at these locations, by UN/LOCODE. */
  unlocodes?: string[] | null;
  /** Only facilities of these types. */
  types?: WaykiteFacilityType[] | null;
  /** Only facilities in these statuses. */
  statuses?: WaykiteFacilityStatus[] | null;
  /** Only facilities handling any of these kinds of cargo. */
  cargo?: WaykiteFacilityCargo[] | null;
  /** @default 50 */
  limit?: number;
  /** @default 0 */
  offset?: number;
};

export type WaykiteNearbyFacilityParams = {
  lat: number;
  lon: number;
  countries?: string[] | null;
  types?: WaykiteFacilityType[] | null;
  statuses?: WaykiteFacilityStatus[] | null;
  /** Only facilities handling any of these kinds of cargo. */
  cargo?: WaykiteFacilityCargo[] | null;
  /** How far to look, in kilometres. */
  maxDistanceKm?: number;
  /** @default 10 */
  limit?: number;
};

export type WaykiteClientOptions = {
  /**
   * The site to call, e.g. your own server when it passes calls on.
   * @default 'https://waykite.com'
   */
  baseUrl?: string;
  /**
   * Your API key, sent as a bearer token: a publishable key (`wk_pk_`) in a browser, which works only from the origins
   * listed on it, or a secret key (`wk_sk_`) on a server, never in a browser. Make one on your waykite dashboard. `null`
   * when the calls go through your own server, which adds the key.
   */
  apiKey: string | null;
  /** The `fetch` to use, e.g. one with retries or a test's stand-in; defaults to the global one. */
  fetch?: typeof fetch;
};

/** The request to the API failed: `status` is its HTTP status, 0 when it never got an answer. */
export class WaykiteError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'WaykiteError';
    this.status = status;
  }
}

type Request = <T extends z.ZodType>(path: string, schema: T, init?: RequestInit) => Promise<z.infer<T>>;

/** A lookup's result, or `null` when it fails, for code that only needs to know whether a record exists. */
async function orNull<T>(lookup: () => Promise<T>): Promise<T | null> {
  try {
    return await lookup();
  } catch {
    return null;
  }
}

function appendAll(params: URLSearchParams, name: string, values: readonly string[] | null | undefined) {
  for (const value of values ?? []) params.append(name, value);
}

/** Locations: ports, airports and other places with a UN/LOCODE. */
export class WaykiteLocationsApi {
  readonly #request: Request;

  constructor(request: Request) {
    this.#request = request;
  }

  /** A location by its code, e.g. `NLRTM`. Throws a `WaykiteError` when there is none (status 404) or the call fails. */
  get = (code: string, init?: RequestInit): Promise<WaykiteLocation> =>
    this.#request(`/api/v1/locations/${encodeURIComponent(code)}`, WaykiteLocationSchema, init);

  /** A location by its code, or `null` when the code is empty or unknown, or the API cannot be reached. */
  tryGet = async (code: string | null | undefined, init?: RequestInit): Promise<WaykiteLocation | null> =>
    code ? orNull(() => this.get(code, init)) : null;

  /** A code as people read it, e.g. "Rotterdam, Netherlands (NLRTM)", or the bare code when it does not resolve. */
  format = async (code: string | null | undefined, init?: RequestInit): Promise<string | null> => {
    const entry = await this.tryGet(code, init);
    return entry ? `${entry.display_name} (${entry.code})` : (code ?? null);
  };

  /** Locations matching a name or code, best match first, a page at a time. */
  search = (
    { query, countries, functions, limit = 50, offset = 0 }: WaykiteLocationSearchParams,
    init?: RequestInit,
  ): Promise<WaykiteLocationSearchResult> => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    appendAll(params, 'country', countries);
    appendAll(params, 'function', functions);
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    return this.#request(`/api/v1/locations/search?${params}`, WaykiteLocationSearchResultSchema, init);
  };

  /** The locations nearest a point, closest first, each with its `distance_km`. */
  nearby = async (
    { lat, lon, countries, functions, maxDistanceKm, limit = 10 }: WaykiteNearbyLocationParams,
    init?: RequestInit,
  ): Promise<WaykiteNearbyLocation[]> => {
    const params = new URLSearchParams({ lat: String(lat), lon: String(lon), limit: String(limit) });
    appendAll(params, 'country', countries);
    appendAll(params, 'function', functions);
    if (maxDistanceKm !== undefined) params.set('max_distance_km', String(maxDistanceKm));
    const { results } = await this.#request(`/api/v1/locations/nearby?${params}`, NearbyLocationsSchema, init);
    return results;
  };

  /** Every location in one download, a few megabytes compressed, e.g. to sync a local copy. Needs a secret key. */
  dataset = (init?: RequestInit): Promise<WaykiteLocationDataset> =>
    this.#request('/data/unlocode.json', WaykiteLocationDatasetSchema, init);
}

/** Facilities, such as container terminals, each identified by its own code. */
export class WaykiteFacilitiesApi {
  readonly #request: Request;

  constructor(request: Request) {
    this.#request = request;
  }

  /**
   * A facility by its code, e.g. `DEHAMEGH`. Throws a `WaykiteError` when there is none (status 404) or the call
   * fails.
   */
  get = (code: string, init?: RequestInit): Promise<WaykiteFacility> =>
    this.#request(`/api/v1/facilities/${encodeURIComponent(code)}`, WaykiteFacilitySchema, init);

  /** A facility by its code, or `null` when there is none or the API cannot be reached. */
  tryGet = async (code: string | null | undefined, init?: RequestInit): Promise<WaykiteFacility | null> =>
    code ? orNull(() => this.get(code, init)) : null;

  /**
   * A code as people read it, e.g. "Eurogate Container Terminal Hamburg (DEHAMEGH)", or the bare code when it does not
   * resolve.
   */
  format = async (code: string | null | undefined, init?: RequestInit): Promise<string | null> => {
    const facility = await this.tryGet(code, init);
    return facility ? `${facility.display_name} (${facility.code})` : (code ?? null);
  };

  /** Facilities matching a name, operator or code, a page at a time. */
  search = (
    { query, countries, unlocodes, types, statuses, cargo, limit = 50, offset = 0 }: WaykiteFacilitySearchParams,
    init?: RequestInit,
  ): Promise<WaykiteFacilitySearchResult> => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    appendAll(params, 'country', countries);
    appendAll(params, 'unlocode', unlocodes);
    appendAll(params, 'type', types);
    appendAll(params, 'status', statuses);
    appendAll(params, 'cargo', cargo);
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    return this.#request(`/api/v1/facilities/search?${params}`, WaykiteFacilitySearchResultSchema, init);
  };

  /** The facilities nearest a point, closest first, each with its `distance_km`. */
  nearby = async (
    { lat, lon, countries, types, statuses, cargo, maxDistanceKm, limit = 10 }: WaykiteNearbyFacilityParams,
    init?: RequestInit,
  ): Promise<WaykiteNearbyFacility[]> => {
    const params = new URLSearchParams({ lat: String(lat), lon: String(lon), limit: String(limit) });
    appendAll(params, 'country', countries);
    appendAll(params, 'type', types);
    appendAll(params, 'status', statuses);
    appendAll(params, 'cargo', cargo);
    if (maxDistanceKm !== undefined) params.set('max_distance_km', String(maxDistanceKm));
    const { results } = await this.#request(`/api/v1/facilities/nearby?${params}`, NearbyFacilitiesSchema, init);
    return results;
  };

  /** Every facility in one download, e.g. to sync a local copy. Needs a secret key. */
  dataset = (init?: RequestInit): Promise<WaykiteFacilityDataset> =>
    this.#request('/data/facilities.json', WaykiteFacilityDatasetSchema, init);
}

/**
 * Ships at ports and bound for them. A port that is part of a larger one, such as a terminal's own code, answers for
 * that one. Throws a `WaykiteError` with status 403 when the key's organization does not have them, and 404 for a code
 * no location has.
 */
export class WaykitePortsApi {
  readonly #request: Request;

  constructor(request: Request) {
    this.#request = request;
  }

  /** The ships expected at a port within the hours ahead, soonest first. */
  arrivals = (
    code: string,
    { withinHours = 168 }: WaykitePortArrivalsParams = {},
    init?: RequestInit,
  ): Promise<WaykitePortArrivals> =>
    this.#request(
      `/api/v1/ports/${encodeURIComponent(code)}/arrivals?within=${withinHours}`,
      WaykitePortArrivalsSchema,
      init,
    );

  /** The calls at a port still open, and those that ended within the hours past, latest first. */
  calls = (
    code: string,
    { withinHours = 168, types }: WaykitePortCallsParams = {},
    init?: RequestInit,
  ): Promise<WaykitePortCalls> => {
    const params = new URLSearchParams({ within: String(withinHours) });
    appendAll(params, 'type', types);
    return this.#request(`/api/v1/ports/${encodeURIComponent(code)}/calls?${params}`, WaykitePortCallsSchema, init);
  };
}

/**
 * Ships, by their IMO number (7 digits) or MMSI (9 digits): who they are, where they are, and where they are going.
 * Throws a `WaykiteError` with status 403 when the key's organization does not have them, and 404 when no ship is
 * known by the number.
 */

export class WaykiteVesselsApi {
  readonly #request: Request;

  constructor(request: Request) {
    this.#request = request;
  }

  /** Who a ship is, by its IMO number or MMSI, e.g. `imo:9811000` or `mmsi:353136000`. */
  get = (id: WaykiteVesselId, init?: RequestInit): Promise<WaykiteVessel> =>
    this.#request(`/api/v1/vessels/${encodeURIComponent(id)}`, WaykiteVesselSchema, init);

  /** Where a ship was last heard, and the port it is in. */
  position = (id: WaykiteVesselId, init?: RequestInit): Promise<WaykiteVesselPosition> =>
    this.#request(`/api/v1/vessels/${encodeURIComponent(id)}/position`, WaykiteVesselPositionSchema, init);

  /** Where a ship is going, and when. */
  voyage = (id: WaykiteVesselId, init?: RequestInit): Promise<WaykiteVesselVoyage> =>
    this.#request(`/api/v1/vessels/${encodeURIComponent(id)}/voyage`, WaykiteVesselVoyageSchema, init);

  /** A ship's yearly reports of the fuel it burned and the greenhouse gases it emitted, newest first. */
  emissions = (id: WaykiteVesselId, init?: RequestInit): Promise<WaykiteVesselEmissions> =>
    this.#request(`/api/v1/vessels/${encodeURIComponent(id)}/emissions`, WaykiteVesselEmissionsSchema, init);
}

const jsonBody = (method: string, body: unknown, init?: RequestInit): RequestInit => ({
  ...init,
  method,
  headers: { ...Object.fromEntries(new Headers(init?.headers)), 'content-type': 'application/json' },
  body: JSON.stringify(body),
});

/** Webhook endpoints, managed with a secret key from a server. */
export class WaykiteWebhookEndpointsApi {
  readonly #request: Request;

  constructor(request: Request) {
    this.#request = request;
  }

  list = (init?: RequestInit): Promise<{ endpoints: WaykiteWebhookEndpoint[] }> =>
    this.#request('/api/v1/webhooks/endpoints', z.object({ endpoints: WaykiteWebhookEndpointSchema.array() }), init);

  get = (id: string, init?: RequestInit): Promise<WaykiteWebhookEndpoint> =>
    this.#request(`/api/v1/webhooks/endpoints/${encodeURIComponent(id)}`, WaykiteWebhookEndpointSchema, init);

  /** Returns the endpoint with its signing secret. */
  create = (
    params: WaykiteWebhookEndpointParams,
    init?: RequestInit,
  ): Promise<{ endpoint: WaykiteWebhookEndpoint; secret: string }> =>
    this.#request(
      '/api/v1/webhooks/endpoints',
      z.object({ endpoint: WaykiteWebhookEndpointSchema, secret: z.string() }),
      jsonBody('POST', params, init),
    );

  /** Changes what is given; `enabled: true` turns one back on. */
  update = (
    id: string,
    params: Partial<WaykiteWebhookEndpointParams> & { enabled?: boolean },
    init?: RequestInit,
  ): Promise<WaykiteWebhookEndpoint> =>
    this.#request(
      `/api/v1/webhooks/endpoints/${encodeURIComponent(id)}`,
      WaykiteWebhookEndpointSchema,
      jsonBody('PATCH', params, init),
    );

  delete = (id: string, init?: RequestInit): Promise<{ id: string; deleted: true }> =>
    this.#request(
      `/api/v1/webhooks/endpoints/${encodeURIComponent(id)}`,
      z.object({ id: z.string(), deleted: z.literal(true) }),
      { ...init, method: 'DELETE' },
    );

  secret = (id: string, init?: RequestInit): Promise<WaykiteWebhookSecret> =>
    this.#request(`/api/v1/webhooks/endpoints/${encodeURIComponent(id)}/secret`, WaykiteWebhookSecretSchema, init);

  /** A new secret; the old one keeps signing for `graceHours`: 0, 1, 24 or 168. */
  roll = (id: string, graceHours: 0 | 1 | 24 | 168, init?: RequestInit): Promise<{ secret: string }> =>
    this.#request(
      `/api/v1/webhooks/endpoints/${encodeURIComponent(id)}/roll`,
      z.object({ secret: z.string() }),
      jsonBody('POST', { grace_hours: graceHours }, init),
    );

  /** Sends the endpoint a `webhook.test` event; returns its delivery's id. */
  test = (id: string, init?: RequestInit): Promise<{ delivery_id: string }> =>
    this.#request(`/api/v1/webhooks/endpoints/${encodeURIComponent(id)}/test`, z.object({ delivery_id: z.string() }), {
      ...init,
      method: 'POST',
    });
}

/** Deliveries to webhook endpoints, kept for 30 days. */
export class WaykiteWebhookDeliveriesApi {
  readonly #request: Request;

  constructor(request: Request) {
    this.#request = request;
  }

  /** The latest first, across endpoints or for one. */
  list = (
    { endpointId, limit = 50 }: { endpointId?: string; limit?: number } = {},
    init?: RequestInit,
  ): Promise<{ deliveries: WaykiteWebhookDelivery[] }> => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (endpointId) params.set('endpoint', endpointId);
    return this.#request(
      `/api/v1/webhooks/deliveries?${params}`,
      z.object({ deliveries: WaykiteWebhookDeliverySchema.array() }),
      init,
    );
  };

  /** Sends a delivery again, with the same `webhook-id` and a fresh set of retries. */
  resend = (id: string, init?: RequestInit): Promise<{ delivery_id: string }> =>
    this.#request(
      `/api/v1/webhooks/deliveries/${encodeURIComponent(id)}/resend`,
      z.object({ delivery_id: z.string() }),
      { ...init, method: 'POST' },
    );
}

/** The ports and ships an organization watches, which its webhook endpoints hear about. Needs a secret key. */
export class WaykiteWatchlistApi {
  readonly #request: Request;

  constructor(request: Request) {
    this.#request = request;
  }

  list = (init?: RequestInit): Promise<{ watches: WaykiteWatch[] }> =>
    this.#request('/api/v1/watchlist', z.object({ watches: WaykiteWatchSchema.array() }), init);

  get = (id: string, init?: RequestInit): Promise<WaykiteWatch> =>
    this.#request(`/api/v1/watchlist/${encodeURIComponent(id)}`, WaykiteWatchSchema, init);

  /** A port by UN/LOCODE or a ship by `imo:` or `mmsi:`; one already watched comes back as it is. */
  add = (
    params: ({ port: string } | { vessel: WaykiteVesselId }) & { topics?: WaykiteWatchTopic[] },
    init?: RequestInit,
  ): Promise<WaykiteWatch> => this.#request('/api/v1/watchlist', WaykiteWatchSchema, jsonBody('POST', params, init));

  update = (id: string, params: { topics: WaykiteWatchTopic[] }, init?: RequestInit): Promise<WaykiteWatch> =>
    this.#request(`/api/v1/watchlist/${encodeURIComponent(id)}`, WaykiteWatchSchema, jsonBody('PATCH', params, init));

  remove = (id: string, init?: RequestInit): Promise<{ id: string; deleted: true }> =>
    this.#request(
      `/api/v1/watchlist/${encodeURIComponent(id)}`,
      z.object({ id: z.string(), deleted: z.literal(true) }),
      { ...init, method: 'DELETE' },
    );
}

/** A typed client for the waykite API, for a server or a browser; responses are checked with zod. */
export class WaykiteClient {
  readonly baseUrl: string;
  readonly locations: WaykiteLocationsApi;
  readonly facilities: WaykiteFacilitiesApi;
  readonly ports: WaykitePortsApi;
  readonly vessels: WaykiteVesselsApi;
  readonly watchlist: WaykiteWatchlistApi;
  readonly webhooks: { endpoints: WaykiteWebhookEndpointsApi; deliveries: WaykiteWebhookDeliveriesApi };

  constructor({ baseUrl = WAYKITE_URL, apiKey, fetch: fetcher }: WaykiteClientOptions) {
    this.baseUrl = baseUrl.replace(/\/$/, '');
    const send = fetcher ?? ((...args: Parameters<typeof fetch>) => fetch(...args));

    const request: Request = async (path, schema, init) => {
      const headers = new Headers(init?.headers);
      headers.set('accept', 'application/json');
      if (apiKey) headers.set('authorization', `Bearer ${apiKey}`);

      const response = await send(`${this.baseUrl}${path}`, { ...init, headers }).catch((error: unknown) => {
        throw new WaykiteError(error instanceof Error ? error.message : 'Request failed', 0);
      });
      if (!response.ok) throw new WaykiteError(`${response.status} ${response.statusText}`.trim(), response.status);
      // The API answers a lookup of an unknown code with 204 and no body.
      if (response.status === 204) throw new WaykiteError('Not found', 404);
      return schema.parse(await response.json());
    };

    this.locations = new WaykiteLocationsApi(request);
    this.facilities = new WaykiteFacilitiesApi(request);
    this.ports = new WaykitePortsApi(request);
    this.vessels = new WaykiteVesselsApi(request);
    this.watchlist = new WaykiteWatchlistApi(request);
    this.webhooks = {
      endpoints: new WaykiteWebhookEndpointsApi(request),
      deliveries: new WaykiteWebhookDeliveriesApi(request),
    };
  }
}
